import { UserAgent, Inviter, Registerer, SessionState } from 'sip.js/lib/api';

const incomingRing = '/audio/voip-incoming-ring.mp3';
const outgoingRingback = '/audio/voip-ringback.mp3';
const endCallBeeps = '/audio/voip-end-call.mp3';

const audioTrack = document.createElement('audio');
audioTrack.setAttribute('type', 'audio/mpeg');
audioTrack.setAttribute('preload', 'metadata');

class SIPClient {
  constructor() {
    this.userAgent = null;
    this.registerer = null;
    this.activeCall = null;
    this.incomingCall = null;
    this.listeners = {};
    this._inited = false;
    this._domain = '';
    this._watchdogInterval = null;
    this._repairRunning = false;
    this.fetchTurnCredentialsFn = null;
    this._turnCache = null; // { iceServers, expiresAt }
    this._turnRefreshInFlight = null;
    this._iceRecoveryInProgress = false;
    this._lastIceRecoveryAt = 0;
    this.silentMode = false;
    this.dndMode = false;
  }

  on(event, cb) {
    if (!this.listeners[event]) this.listeners[event] = new Set();
    this.listeners[event].add(cb);
  }

  off(event, cb) {
    if (this.listeners[event]) this.listeners[event].delete(cb);
  }

  emit(event, payload) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach((cb) => {
      try { cb(payload); } catch (e) { console.error(e); }
    });
  }

  async init({ domain, extension, password, proxy, isNativeApp = false, ...rest }) {
    if (this._inited) return; // idempotent
    this._inited = true;

    this._domain = domain;
    this._isNativeApp = !!isNativeApp;
    this._initOptions = { domain, extension, password, proxy, ...rest };

    // Pre-ask for mic so answering isn’t blocked by autoplay policies
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
    } catch (e) {
      console.warn('Mic pre-access failed (will request on demand).', e); // eslint-disable-line no-console
    }

    // Build a fully-wired UA (delegate INCLUDED)
    this.userAgent = this._buildUserAgentFromInitOptions();
    this.registerer = new Registerer(this.userAgent);

    // Start + register immediately (init must work deterministically)
    await this.userAgent.start();
    await this.registerer.register();

    // Visibility-based recovery (NO delegate / UA rebuilding here)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      if (this.dndMode) return;

      const t = this.userAgent?.transport;
      const reg = this.registerer;

      if (!t || t.state !== 'Connected') {
        console.warn('=== visible → ensure UA ==='); // eslint-disable-line no-console
        this._ensureUAAndRegisterer().catch(() => {});
        return;
      }

      console.warn(`=== visible → re-register === ${reg?.state}`); // eslint-disable-line no-console
      this._ensureUAAndRegisterer().catch(() => {});
    });

    // Network stack changed → hard restart
    window.addEventListener('online', () => {
      if (this.dndMode) return;
      console.warn('=== browser online → HARD RESTART ==='); // eslint-disable-line no-console
      this._restartUAHard();
    });

    this._startWatchdog();
  }

  _buildUserAgentFromInitOptions() {
    const { domain, extension, password, proxy } = this._initOptions;

    const websocket = `wss://${proxy}:7443`;
    const uri = UserAgent.makeURI(`sip:${extension}@${domain}`);
    if (!uri) throw new Error('Invalid SIP URI.');

    const ua = new UserAgent({
      uri,
      transportOptions: { server: websocket },
      authorizationUsername: extension,
      authorizationPassword: password,
      contactParams: { transport: 'ws' },
      viaHost: proxy,
      registrarServer: proxy,
      displayName: extension,
      contactName: extension,
    });

    // ALWAYS attach delegate BEFORE start()
    ua.delegate = {
      onInvite: async (invitation) => {
        // If DND is on, auto-reject so the call rolls to voicemail without
        // ringing the client. Belt-and-suspenders against any path where
        // FusionPBX still delivers an INVITE despite the unregistered state.
        if (this.dndMode) {
          try {
            await invitation.reject();
          } catch (e) {
            // ignore — the server will time out and roll to voicemail
          }
          return;
        }

        await this._refreshTurnConfig();

        this.incomingCall = invitation;
        this._playAudio('ring');
        this._attachRemoteAudio(invitation);

        invitation.delegate = { // eslint-disable-line no-param-reassign
          onSessionDescriptionHandler: (sdh) => {
            this._attachIceDebugging(sdh);

            const stream = sdh.localMediaStream;
            const track = stream?.getAudioTracks()?.[0];
            if (track) {
              track.applyConstraints({
                echoCancellation: true,
                noiseSuppression: false,
                autoGainControl: false,
                channelCount: 1,
              }).catch(() => {});
            }
          },
        };

        invitation.stateChange.addListener((state) => {
          if (state === SessionState.Established) {
            this.activeCall = invitation;
            this.emit('callConnected', invitation);
          }
          if (state === SessionState.Terminated) {
            this._playEndCall(invitation);
            this.activeCall = null;
            this.incomingCall = null;
            this.emit('callEnded');
          }
        });

        this.emit('incomingCall', invitation);
      },
    };

    return ua;
  }

  async fetchTurnCredentials() {
    if (!this.fetchTurnCredentialsFn) {
      throw new Error('TURN credential provider not configured');
    }

    return this.fetchTurnCredentialsFn();
  }

  async _refreshTurnConfig() {
    // Fast path: valid cache
    if (this._isTurnCacheValid()) {
      console.log('Using Cached TURN Servers'); // eslint-disable-line no-console
      this.userAgent.configuration.sessionDescriptionHandlerFactoryOptions = {
        peerConnectionConfiguration: {
          iceTransportPolicy: 'relay',
          iceServers: this._turnCache.iceServers,
        },
        iceGatheringTimeout: 10000,
      };
      return;
    }

    // Deduplicate concurrent refreshes
    if (this._turnRefreshInFlight) {
      await this._turnRefreshInFlight;
      return;
    }

    this._turnRefreshInFlight = (async () => {
      console.log('Refreshing TURN Servers'); // eslint-disable-line no-console
      const { objects: iceServers } = await this.fetchTurnCredentials();

      if (!iceServers?.length) {
        throw new Error('TURN API returned no servers');
      }

      // TTL is per RFC in seconds
      const ttlSeconds = iceServers[0].ttl ?? 60;
      const expiresAt = Date.now() + ttlSeconds * 1000;

      this._turnCache = {
        iceServers,
        expiresAt,
      };

      this.userAgent.configuration.sessionDescriptionHandlerFactoryOptions = {
        peerConnectionConfiguration: {
          iceTransportPolicy: 'relay',
          iceServers,
        },
        iceGatheringTimeout: 10000,
      };
    })();

    try {
      await this._turnRefreshInFlight;
    } finally {
      this._turnRefreshInFlight = null;
    }
  }

  _isTurnCacheValid() {
    if (!this._turnCache) return false;
    const SKEW_MS = 30000; // 30s
    return Date.now() < (this._turnCache.expiresAt - SKEW_MS);
  }

  async _createInviterWithTurn(dest) {
    // Ensure TURN creds are fresh
    await this._refreshTurnConfig();

    // Create inviter
    const inviter = new Inviter(this.userAgent, dest, {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: {
            echoCancellation: true,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: 1,
          },
          video: false,
        },
      },
    });

    // Attach ICE debugging exactly once per session
    inviter.delegate = inviter.delegate || {};
    inviter.delegate.onSessionDescriptionHandler = (sdh) => {
      this._attachIceDebugging(sdh);

      const stream = sdh.localMediaStream;
      const track = stream?.getAudioTracks()?.[0];
      if (track) {
        track.applyConstraints({
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
        }).catch(() => {});
      }
    };

    return inviter;
  }

  /* eslint-disable no-param-reassign */
  _attachIceDebugging(sdh) {
    if (sdh.__iceDebugAttached) return;
    sdh.__iceDebugAttached = true;

    const pc = sdh.peerConnection;
    if (!pc) return;

    // Connection / signaling states. Every call is relayed (iceTransportPolicy
    // 'relay'), so losing the TURN box that holds our allocation surfaces here:
    // restart ICE to re-gather on a surviving pool member instead of letting
    // the call die. Also fed by the inbound-RTP watchdog below, which reacts
    // faster than the browser's failed timer.
    let disconnectedTimer = null;
    pc.addEventListener('iceconnectionstatechange', () => {
      console.log('[ICE] connection state:', pc.iceConnectionState); // eslint-disable-line no-console
      clearTimeout(disconnectedTimer);
      if (pc.iceConnectionState === 'failed') {
        this._recoverIce(pc, 'ice failed');
      } else if (pc.iceConnectionState === 'disconnected') {
        disconnectedTimer = setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected') this._recoverIce(pc, 'ice disconnected');
        }, 3000);
      }
    });
    this._startRtpWatchdog(pc);

    pc.addEventListener('icegatheringstatechange', () => {
      console.log('[ICE] gathering state:', pc.iceGatheringState); // eslint-disable-line no-console
    });

    pc.addEventListener('connectionstatechange', () => {
      console.log('[PC] connection state:', pc.connectionState); // eslint-disable-line no-console
    });

    pc.addEventListener('signalingstatechange', () => {
      console.log('[PC] signaling state:', pc.signalingState); // eslint-disable-line no-console
    });

    // TURN / STUN errors
    pc.addEventListener('icecandidateerror', (e) => {
      console.error('[ICE] candidate error', {
        url: e.url,
        errorCode: e.errorCode,
        errorText: e.errorText,
        address: e.address,
        port: e.port,
      }); // eslint-disable-line no-console
    });

    // ICE candidates (this is the important part)
    pc.addEventListener('icecandidate', (e) => {
      if (!e.candidate) {
        console.log('[ICE] candidate gathering complete'); // eslint-disable-line no-console
        return;
      }

      const c = e.candidate;

      // Raw candidate line (best single source of truth)
      console.log('[ICE] candidate raw:', c.candidate); // eslint-disable-line no-console

      // Parsed fields (easy to read)
      console.log('[ICE] candidate details:', { // eslint-disable-line no-console
        type: c.type,
        protocol: c.protocol,
        address: c.address,
        port: c.port,
        foundation: c.foundation,
        priority: c.priority,
        component: c.component,
        tcpType: c.tcpType || null,
        relatedAddress: c.relatedAddress || null,
        relatedPort: c.relatedPort || null,
      });

      // Helpful one-line summary
      console.log( // eslint-disable-line no-console
        `[ICE] ${c.type.toUpperCase()} ${c.protocol.toUpperCase()} ${c.address}:${c.port}`,
      );
    });
  }

  _sessionForPeerConnection(pc) {
    return [this.activeCall, this._consultCall, this.incomingCall]
      .find((s) => s?.sessionDescriptionHandler?.peerConnection === pc) || null;
  }

  _startRtpWatchdog(pc) {
    let lastPackets = -1;
    let stalled = 0;
    const interval = setInterval(async () => {
      if (pc.connectionState === 'closed' || pc.signalingState === 'closed') {
        clearInterval(interval);
        return;
      }
      if (this._isHeld || pc.iceConnectionState !== 'connected') return;
      try {
        const stats = await pc.getStats();
        let packets = -1;
        stats.forEach((r) => {
          if (r.type === 'inbound-rtp' && r.kind === 'audio') packets = r.packetsReceived;
        });
        if (packets >= 0 && packets === lastPackets) {
          stalled += 1;
          if (stalled >= 2) this._recoverIce(pc, 'inbound rtp stalled'); // ~4s silent
        } else {
          stalled = 0;
        }
        lastPackets = packets;
      } catch (e) {
        // stats unavailable this tick
      }
    }, 2000);
  }

  async _recoverIce(pc, reason) {
    const session = this._sessionForPeerConnection(pc);
    if (!session || session.state !== SessionState.Established) return;
    if (this._iceRecoveryInProgress || Date.now() - this._lastIceRecoveryAt < 15000) return;
    this._iceRecoveryInProgress = true;
    this._lastIceRecoveryAt = Date.now();

    let path = 'unknown';
    try {
      const stats = await pc.getStats();
      let pair = null;
      stats.forEach((r) => {
        if (r.type === 'candidate-pair' && (r.nominated || r.state === 'succeeded') && !pair) pair = r;
      });
      const local = pair && stats.get(pair.localCandidateId);
      if (local) path = `${local.candidateType} ${local.address || local.ip}:${local.port}`;
    } catch (e) { /* best effort */ }
    console.warn(`[ICE] restart (${reason}) from ${path}`); // eslint-disable-line no-console

    try {
      // Fresh TURN cred (host-agnostic HMAC) so re-gathering can authenticate
      // against whichever pool member the NLB hands us next.
      await this._refreshTurnConfig();
      pc.setConfiguration({ ...pc.getConfiguration(), iceServers: this._turnCache.iceServers });
      await session.invite({
        sessionDescriptionHandlerOptions: {
          offerOptions: { iceRestart: true },
          hold: !!this._isHeld,
          constraints: {
            audio: {
              echoCancellation: true,
              noiseSuppression: false,
              autoGainControl: false,
              channelCount: 1,
            },
            video: false,
          },
        },
      });
      console.log('[ICE] restart re-INVITE sent'); // eslint-disable-line no-console
    } catch (err) {
      console.error('[ICE] restart failed', err); // eslint-disable-line no-console
    } finally {
      this._iceRecoveryInProgress = false;
    }
  }

  async start() {
    if (!this.userAgent) throw new Error('SIPClient not initialized');
    await this.userAgent.start();
    if (!this.dndMode) {
      await this.registerer.register();
    }
    this.emit('ready');
  }

  async unregister() {
    if (this.registerer) {
      await this.registerer.unregister();
    }
  }

  async register() {
    // DND must short-circuit every path that could re-register, otherwise a
    // recovery handler / internal refresh that fires after an inbound call
    // (e.g. one that rolled to voicemail) will silently bring registration back.
    if (this.dndMode) return;
    if (this.registerer) {
      await this.registerer.register();
      this.emit('ready');
    }
  }

  async stop() {
    try {
      if (this.registerer) await this.registerer.unregister();
      if (this.userAgent) await this.userAgent.stop();
    } finally {
      this.activeCall = null;
      this.incomingCall = null;
      this.emit('stopped');
      this._inited = false;
    }
  }

  async call(target) {
    const dest = UserAgent.makeURI(`sip:${target}@${this._domain}`);
    if (!dest) throw new Error('Invalid target');

    const inviter = await this._createInviterWithTurn(dest);

    this._attachRemoteAudio(inviter);

    const requestDelegate = {
      onProgress: (response) => {
        const code = response.message?.statusCode;
        if (code === 180 || code === 183) this._playAudio('ringback');
      },
      onAccept: () => this._stopAudio(),
      onReject: () => this._stopAudio(),
    };

    inviter._outgoingTarget = target;
    inviter.direction = 'outgoing';

    this.activeCall = inviter;

    inviter.stateChange.addListener((state) => {
      if (state === SessionState.Established) {
        this._stopAudio(); // stop ringback
        const sdh = inviter.sessionDescriptionHandler;
        if (sdh && sdh.remoteMediaStream) {
          const audioEl = document.getElementById('remoteAudio');
          if (audioEl) {
            audioEl.srcObject = sdh.remoteMediaStream;
            audioEl.play().catch((e) => console.warn('play() failed:', e)); // eslint-disable-line no-console
          }
        }
        this.emit('callConnected', inviter); // eslint-disable-line no-console
      } else if (state === SessionState.Terminated) {
        this._stopAudio(); // stop ringback
        this._playEndCall(inviter);
        this.activeCall = null;
        this.emit('callEnded');
      }
    });

    this.emit('outgoingCall', inviter, target);
    await this._logMediaSnapshot('before-invite');

    try {
      await inviter.invite({ requestDelegate });
    } catch (e) {
      console.error('[INVITE] failed', { name: e?.name, message: e?.message, e }); // eslint-disable-line no-console
      await this._logMediaSnapshot('after-invite-fail');
      throw e;
    }
    return inviter;
  }

  async answer() {
    if (!this.incomingCall) return;

    this._stopAudio();
    const call = this.incomingCall;

    // Confirm we still have a mic device right now
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasMic = devices.some((d) => d.kind === 'audioinput');

    await this._logMediaSnapshot('before-accept');

    try {
      await call.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: hasMic,
            video: false,
          },
        },
      });
    } catch (e) {
      // Retry once with the loosest possible audio constraint
      if (e?.name === 'NotFoundError') {
        await call.accept({
          sessionDescriptionHandlerOptions: {
            constraints: { audio: true, video: false },
          },
        });
        return;
      }
      await this._logMediaSnapshot('after-accept-fail');
      throw e;
    }

    this._attachRemoteAudio(call);
  }

  async reject() {
    if (!this.incomingCall) return;
    this._stopAudio();
    await this.incomingCall.reject();
    this.incomingCall = null;
    this.emit('callRejected');
  }

  async hangup() {
    this._stopAudio(); // stop ringer or ringback

    const call = this.activeCall || this.incomingCall;
    if (!call) {
      console.warn('No active call to hang up.'); // eslint-disable-line no-console
      return;
    }

    try {
      // mark it before termination so _playEndCall() knows
      call._wasLocallyTerminated = true;

      const state = call.state || call.sessionState;
      console.log('Hangup called, session state:', state); // eslint-disable-line no-console

      if (state === SessionState.Initial || state === SessionState.Establishing) {
        if (typeof call.cancel === 'function') {
          await call.cancel();
          console.log('Outgoing call canceled.'); // eslint-disable-line no-console
        } else if (typeof call.reject === 'function') {
          await call.reject();
          console.log('Incoming call rejected.'); // eslint-disable-line no-console
        }
      } else if (state === SessionState.Established) {
        if (typeof call.terminate === 'function') {
          await call.terminate();
          console.log('Call terminated.'); // eslint-disable-line no-console
        } else if (typeof call.bye === 'function') {
          await call.bye();
          console.log('Call hung up.'); // eslint-disable-line no-console
        }
      } else {
        console.log('Call already terminated or invalid state:', state); // eslint-disable-line no-console
      }
    } catch (err) {
      console.error('Hangup failed:', err);
    } finally {
      this.activeCall = null;
      this.incomingCall = null;
      this.emit('callEnded');
    }
  }

  // --- Mute / Unmute local microphone
  async toggleMute() {
    const call = this.activeCall;
    if (!call || !call.sessionDescriptionHandler) return false;

    const pc = call.sessionDescriptionHandler.peerConnection;
    const senders = pc.getSenders ? pc.getSenders() : [];

    let isNowMuted = false;
    senders.forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !sender.track.enabled; // eslint-disable-line no-param-reassign
        isNowMuted = !sender.track.enabled;
      }
    });

    this.emit('muteToggled', isNowMuted);
    return isNowMuted;
  }

  // --- Hold / Unhold via SIP re-INVITE
  async toggleHold() {
    const session = this.activeCall;
    if (!session) return false;

    const sdh = session.sessionDescriptionHandler;
    if (!sdh) return false;

    // Determine current local media direction
    const isHeld = this._isHeld || false;
    // const newDirection = isHeld ? 'sendrecv' : 'sendonly';

    try {
      // Update SDP to change direction (this triggers a re-INVITE)
      const options = {
        sessionDescriptionHandlerOptions: {
          hold: !isHeld,
          constraints: {
            audio: {
              echoCancellation: true,
              noiseSuppression: false,
              autoGainControl: false,
              channelCount: 1,
            },
            video: false,
          },
        },
      };

      await session.invite(options); // re-INVITE with new SDP

      this._isHeld = !isHeld;
      this.emit('holdToggled', this._isHeld);
      console.log(`Call ${this._isHeld ? 'placed on hold' : 'resumed'}.`); // eslint-disable-line no-console
      return this._isHeld;
    } catch (err) {
      console.error('Hold/unhold failed:', err);
      return isHeld;
    }
  }

  // --- Send DTMF tone during active call
  async sendDTMF(tone) {
    const call = this.activeCall;
    if (!call || !call.sessionDescriptionHandler) {
      console.warn('No active call to send DTMF.'); // eslint-disable-line no-console
      return;
    }

    try {
      const pc = call.sessionDescriptionHandler.peerConnection;
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
      if (!sender || !sender.dtmf) {
        console.warn('DTMF sender not available.'); // eslint-disable-line no-console
        return;
      }

      sender.dtmf.insertDTMF(tone);
      this.emit('dtmfSent', tone);
      console.log(`Sent DTMF: ${tone}`); // eslint-disable-line no-console
    } catch (err) {
      console.error('Failed to send DTMF:', err);
    }
  }

  // --- Blind (unattended) transfer of the current call to `target`
  async blindTransfer(target) {
    const session = this.activeCall;
    if (!session) {
      console.warn('No active call to transfer.'); // eslint-disable-line no-console
      return false;
    }
    const dest = UserAgent.makeURI(`sip:${target}@${this._domain}`);
    if (!dest) throw new Error('Invalid transfer target');

    try {
      // Sends REFER to remote party with target URI
      const referral = await session.refer(dest, {
        // optional: observe NOTIFYs for final status
        requestDelegate: {
          onAccept: () => this.emit('transferProgress', { type: 'blind', state: 'accepted' }),
          onReject: () => this.emit('transferProgress', { type: 'blind', state: 'rejected' }),
        },
      });

      // Keep UI updated on NOTIFY from the transferee
      referral.delegate = {
        onNotify: (notify) => {
          const code = notify.message?.statusCode;
          if (code) this.emit('transferNotify', { type: 'blind', code });
        },
      };

      // Typically the remote will BYE us once transfer completes.
      this.emit('transferStarted', { type: 'blind', target });
      return true;
    } catch (err) {
      console.error('Blind transfer failed:', err); // eslint-disable-line no-console
      this.emit('transferError', { type: 'blind', error: err });
      return false;
    }
  }

  // Internal holder for consult call/session
  _consultCall = null;
  _isConsulting = false;

  // --- Start attended (consultative) transfer: hold A, call B
  async startConsultTransfer(target) {
    const primary = this.activeCall;
    if (!primary) {
      console.warn('No active call to consult from.'); // eslint-disable-line no-console
      return false;
    }
    if (this._isConsulting) {
      console.warn('Consult already in progress.'); // eslint-disable-line no-console
      return false;
    }

    // Put caller A on hold while we dial B
    try {
      if (!this._isHeld) await this.toggleHold();
    } catch (e) {
      console.warn('Failed to hold primary call before consult; continuing anyway.'); // eslint-disable-line no-console
    }

    const dest = UserAgent.makeURI(`sip:${target}@${this._domain}`);
    if (!dest) throw new Error('Invalid consult target');

    try {
      const inviter = await this._createInviterWithTurn(dest);

      this._attachRemoteAudio(inviter); // so you can hear B when answered
      this._consultCall = inviter;
      this._isConsulting = true;

      inviter.stateChange.addListener((state) => {
        // You can emit finer-grained states if you want
        if (state === SessionState.Established) {
          this.emit('consultConnected', inviter);
        }
        if (state === SessionState.Terminated) {
          this.emit('consultEnded');
          this._consultCall = null;
          this._isConsulting = false;
          // If consult ended without completing transfer, resume A
          if (this._isHeld && this.activeCall) {
            this.toggleHold().catch(() => {});
          }
        }
      });

      // Place consult call
      await inviter.invite({
        requestDelegate: {
          onProgress: () => this.emit('consultProgress', { target }),
          onAccept: () => this.emit('consultAccepted', { target }),
          onReject: () => this.emit('consultRejected', { target }),
        },
      });

      this.emit('consultStarted', { target });
      return true;
    } catch (err) {
      console.error('Consult transfer dial failed:', err); // eslint-disable-line no-console
      this.emit('transferError', { type: 'consult-start', error: err });
      // Attempt to resume A if we had put it on hold
      if (this._isHeld && this.activeCall) {
        await this.toggleHold().catch(() => {});
      }
      this._consultCall = null;
      this._isConsulting = false;
      return false;
    }
  }

  // --- Complete attended transfer: REFER A with Replaces for B
  async completeConsultTransfer() {
    const primary = this.activeCall;
    const consult = this._consultCall;

    if (!primary || !consult) {
      console.warn('No primary/consult call to complete transfer.'); // eslint-disable-line no-console
      return false;
    }

    try {
      // Pass consult Session → sip.js constructs a Refer-To with Replaces
      const referral = await primary.refer(consult, {
        requestDelegate: {
          onAccept: () => this.emit('transferProgress', { type: 'consult', state: 'accepted' }),
          onReject: () => this.emit('transferProgress', { type: 'consult', state: 'rejected' }),
        },
      });

      referral.delegate = {
        onNotify: (notify) => {
          const code = notify.message?.statusCode;
          if (code) this.emit('transferNotify', { type: 'consult', code });
        },
      };

      this.emit('transferStarted', { type: 'consult' });

      // After a successful attended transfer, both our legs will normally end.
      // We don't force-hangup; we let the remote BYE arrive and our existing
      // state listener will clean up. But we can proactively end the consult leg:
      // (If your SBC/FS flow expects you to drop immediately, uncomment below)
      // try { await consult.bye?.(); } catch {}

      return true;
    } catch (err) {
      console.error('Complete attended transfer failed:', err); // eslint-disable-line no-console
      this.emit('transferError', { type: 'consult-complete', error: err });
      return false;
    }
  }

  // --- Cancel the consult leg and resume original caller A
  async cancelConsultTransfer() {
    const consult = this._consultCall;
    if (!consult) return;

    try {
      // Cancel dialing or hang up if connected
      if (consult.state === SessionState.Establishing && typeof consult.cancel === 'function') {
        await consult.cancel();
      } else if (typeof consult.terminate === 'function') {
        await consult.terminate();
      } else if (typeof consult.bye === 'function') {
        await consult.bye();
      }
    } catch (e) {
      console.warn('Failed to stop consult leg:', e); // eslint-disable-line no-console
    } finally {
      this._consultCall = null;
      this._isConsulting = false;
      // Resume A if we had it on hold
      if (this._isHeld && this.activeCall) {
        await this.toggleHold().catch(() => {});
      }
      this.emit('consultCancelled');
    }
  }

  _attachRemoteAudio(session) {
    // Desktop web behavior
    if (!this._isNativeApp) {
      this._attachRemoteAudioWeb(session);
      return;
    }

    // Native — we do not attach audio elements
    this._bindRemoteMediaForNative(session);
  }

  _attachRemoteAudioWeb(session) {
    const audioEl = document.getElementById('remoteAudio');
    if (!audioEl) return;

    // Prefer SIP.js' remoteMediaStream (same as your working code)
    const bindFromSDH = (sdh) => {
      if (!sdh) return;
      // Bind when tracks appear
      const bind = () => {
        if (sdh.remoteMediaStream) {
          audioEl.srcObject = sdh.remoteMediaStream;
          audioEl.autoplay = true;
          audioEl.playsInline = true;
          audioEl.muted = false;
          audioEl.play().catch((e) => console.warn('audio play blocked:', e)); // eslint-disable-line no-console
        }
      };
      // Older/newer builds: either emit on remote stream or just bind immediately
      if (sdh.remoteMediaStream && typeof sdh.remoteMediaStream.addEventListener === 'function') {
        sdh.remoteMediaStream.addEventListener('addtrack', bind, { once: true });
      }
      bind();
    };

    // If SDH already exists, bind now
    if (session.sessionDescriptionHandler) {
      bindFromSDH(session.sessionDescriptionHandler);
    }

    // Also handle the SIP.js hook that fires when the SDH is created
    if (typeof session.delegate === 'object') {
      session.delegate.onSessionDescriptionHandler = (sdh) => { // eslint-disable-line no-param-reassign
        bindFromSDH(sdh);
      };
    }
  }

  async _logMediaSnapshot(tag) {
    try {
      const perm = navigator.permissions
        ? await navigator.permissions.query({ name: 'microphone' }).catch(() => null)
        : null;

      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
      const audioInputs = devices.filter((d) => d.kind === 'audioinput');
      const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');

      console.warn(`[MEDIA:${tag}] permission=`, perm?.state || 'unknown'); // eslint-disable-line no-console
      console.warn( // eslint-disable-line no-console
        `[MEDIA:${tag}] audioinput count=${audioInputs.length}`,
        audioInputs.map((d) => ({ label: d.label, deviceId: d.deviceId })),
      );
      console.warn( // eslint-disable-line no-console
        `[MEDIA:${tag}] audiooutput count=${audioOutputs.length}`,
        audioOutputs.map((d) => ({ label: d.label, deviceId: d.deviceId })),
      );
    } catch (e) {
      console.warn(`[MEDIA:${tag}] snapshot failed`, e); // eslint-disable-line no-console
    }
  }

  _bindRemoteMediaForNative(session) {
    let fired = false;

    const fire = (sdh) => {
      if (fired) return;
      fired = true;

      try {
        this.emit('nativeMedia', {
          type: 'mediaReady',

          // Identify call session
          callId: session.id || session.callId || null,

          // Direction context (IMPORTANT later for CallKit/Telecom)
          direction: session._outgoingTarget ? 'outgoing' : 'incoming',

          // SIP remote identity (if available)
          target: session._outgoingTarget
            || session.remoteIdentity?.uri?.user
            || null,

          // Whether a remote audio stream exists yet
          hasRemoteStream: !!sdh.remoteMediaStream,

          // Stream identifier that RN can look up
          mediaStreamId: sdh.remoteMediaStream?.id || null,
        });
      } catch (e) {
        console.warn('native media emit failed', e); // eslint-disable-line no-console
      }
    };

    // Already have SDP -> fire now
    if (session.sessionDescriptionHandler) {
      fire(session.sessionDescriptionHandler);
    }

    // SDP will arrive after INVITE/accept
    if (typeof session.delegate === 'object') {
      const orig = session.delegate.onSessionDescriptionHandler;
      session.delegate.onSessionDescriptionHandler = (sdh) => { // eslint-disable-line no-param-reassign
        if (typeof orig === 'function') orig(sdh);
        fire(sdh);
      };
    }
  }

  _playEndCall(call) {
    const wasLocal = call?._wasLocallyTerminated || false;

    if (!wasLocal) {
      console.log('Call ended by remote party'); // eslint-disable-line no-console
      this._playAudio('endcall'); // play short end tone
    } else {
      console.log('Call ended locally'); // eslint-disable-line no-console
    }
  }

  _playAudio(id) {
    if (id === 'ring' && this.silentMode) return;

    const soundMap = {
      ring: incomingRing,
      ringback: outgoingRingback,
      endcall: endCallBeeps,
    };
    const soundSource = soundMap[id];
    if (soundSource) {
      audioTrack.setAttribute('src', soundSource);
      audioTrack.loop = id !== 'endcall';
      const promise = audioTrack.play();

      if (promise !== undefined) {
        promise
          .catch((error) => {
            console.warn(`Audio auto-play was prevented: ${error}`); // eslint-disable-line no-console
          });
      }
    }
  }

  _stopAudio() {
    audioTrack.pause();
    audioTrack.currentTime = 0;
  }

  _startWatchdog() {
    if (this._watchdogInterval) return;

    this._watchdogInterval = setInterval(() => {
      const t = this.userAgent?.transport;
      const reg = this.registerer;

      if (this.activeCall || this.incomingCall) return;
      if (this.dndMode) return;

      const isTransportGood = t && t.state === 'Connected';
      const isRegistered = reg && reg.state === 'Registered';

      // PBX DOWN (ALWAYS LOG THIS)
      if (!isTransportGood) {
        console.warn(`=== WATCHDOG: PBX DOWN (transport=${t?.state ?? 'none'}) ===`); // eslint-disable-line no-console

        if (!this._repairRunning) {
          console.warn('=== WATCHDOG: transport down → RESTART ==='); // eslint-disable-line no-console
          this._restartUAHard();
        }

        return;
      }

      // Transport OK, registration missing
      if (!isRegistered) {
        console.warn(`=== WATCHDOG: transport up, NOT registered (reg=${reg?.state ?? 'none'}) ===`); // eslint-disable-line no-console
        this._ensureUAAndRegisterer().catch(() => {});
      }
    }, 30000);
  }

  async _ensureUAAndRegisterer() {
    if (this._repairRunning) return;
    // Defensive: even though every caller of this helper bails on DND, guard
    // here too so a stray invocation can't silently re-register the UA.
    if (this.dndMode) return;

    // Ensure UA exists and is started
    if (!this.userAgent) {
      console.warn('=== ENSURE: rebuilding UA ==='); // eslint-disable-line no-console
      this.userAgent = this._buildUserAgentFromInitOptions();
      this.registerer = new Registerer(this.userAgent);

      try {
        await this.userAgent.start();
        console.warn('=== RESULT: UserAgent.start() OK ==='); // eslint-disable-line no-console
      } catch (err) {
        console.error('=== RESULT: UserAgent.start() FAILED ===', err); // eslint-disable-line no-console
        return;
      }
    } else if (this.userAgent.state !== 'Started') {
      console.warn(`=== ENSURE: UA not started (state=${this.userAgent.state}) ===`); // eslint-disable-line no-console
      return;
    }

    const t = this.userAgent.transport;
    if (!t || t.state !== 'Connected') {
      console.warn(`=== ENSURE: waiting for transport (state=${t?.state ?? 'none'}) ===`); // eslint-disable-line no-console
      return;
    }

    if (!this.registerer) {
      console.warn('=== ENSURE: creating Registerer ==='); // eslint-disable-line no-console
      this.registerer = new Registerer(this.userAgent);
    }

    if (this.registerer.state === 'Registered') {
      console.warn('=== RESULT: already REGISTERED ==='); // eslint-disable-line no-console
      return;
    }

    try {
      console.warn('=== ACTION: sending REGISTER ==='); // eslint-disable-line no-console
      const result = await this.registerer.register();

      const status = result?.message?.statusCode;
      const reason = result?.message?.reasonPhrase;

      if (status >= 200 && status < 300) {
        console.warn(`=== RESULT: REGISTER OK (${status}) ===`); // eslint-disable-line no-console
      } else {
        console.error(`=== RESULT: REGISTER FAILED (${status}) === ${reason}`); // eslint-disable-line no-console
      }
    } catch (err) {
      console.error('=== RESULT: REGISTER TRANSPORT FAILURE ===', err); // eslint-disable-line no-console
    }
  }

  async _restartUAHard() {
    if (this.activeCall || this.incomingCall) {
      console.warn('=== HARD RESTART BLOCKED (active call) ==='); // eslint-disable-line no-console
      return;
    }

    if (this._repairRunning) {
      console.warn('=== HARD RESTART SKIPPED (already running) ==='); // eslint-disable-line no-console
      return;
    }

    this._repairRunning = true;
    this._lastRecoveryResult = 'UNKNOWN';

    console.warn('=== HARD RESTART BEGIN ==='); // eslint-disable-line no-console

    try {
      const t = this.userAgent?.transport;
      const transportState = t?.state ?? 'none';

      if (!this.userAgent) {
        this._lastRecoveryResult = 'NO_EXISTING_UA';
      } else if (transportState === 'Connected') {
        try {
          await this.userAgent.stop();
          this._lastRecoveryResult = 'STOPPED_CONNECTED_UA';
        } catch {
          this._lastRecoveryResult = 'STOP_FAILED';
        }
      } else {
        this._lastRecoveryResult = `SKIPPED_STOP (transport=${transportState})`;
      }

      this.userAgent = null;
      this.registerer = null;

      // If ensure didn’t register, classify WHY
      const nt = this.userAgent?.transport;
      if (!nt) {
        this._lastRecoveryResult = 'NO_TRANSPORT_CREATED';
      } else if (nt.state !== 'Connected') {
        this._lastRecoveryResult = `PBX_UNREACHABLE (transport=${nt.state})`;
      } else if (!this.registerer) {
        this._lastRecoveryResult = 'TRANSPORT_OK_REGISTERER_NOT_CREATED';
      } else if (this.registerer.state !== 'Registered') {
        this._lastRecoveryResult = `REGISTER_FAILED (state=${this.registerer.state})`;
      } else {
        this._lastRecoveryResult = 'RECOVERY_SUCCESS';
      }
    } finally {
      this._repairRunning = false;
      console.warn(`=== HARD RESTART RESULT: ${this._lastRecoveryResult} ===`); // eslint-disable-line no-console
      console.warn('=== HARD RESTART END ==='); // eslint-disable-line no-console
    }
  }
}

export const sipClient = new SIPClient(); // eslint-disable-line import/prefer-default-export
